import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { Grid, Tab } from '@leapeasy/ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo } from 'react';

import { getLandlordDetailAction } from 'store/actions/userActions';
import { failedNotif } from 'store/actions/notifActions';
import { DetailCard } from './DetailCard';
import { ReportTab, BuildingTab, ApplicationTab, PolicyTab, ClaimTab, InvoiceTab } from './Tabs';
import styled from 'styled-components';
import { useDetailRouteTabs } from 'hooks/useDetailRouteTabs';
import { useHydrateDetailsOnce } from 'hooks/useHydrateDetailsOnce';
import { maskDemoField } from 'utils/demoMask';
import { DetailPageShell, SectionPanelSkeleton, TabBarSkeleton } from 'components/DetailPageState';
import { useEntityDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';

const LANDLORD_TAB_SLUGS = ['report', 'properties', 'applications', 'policy', 'claim', 'invoice'];

const TABS = [
  {
    title: 'Report',
    icon: 'General.Report',
  },
  {
    title: 'Properties',
    icon: 'EditorLayout.Apps',
  },
  {
    title: 'Applications',
    icon: 'HomePlaces.MapsHomeWork',
  },
  {
    title: 'Policy',
    icon: 'FileFolder.FindInPage',
  },
  {
    title: 'Claim',
    icon: 'HomePlaces.Gite',
  },
  {
    title: 'Invoice',
    icon: 'FileFolder.Receipt',
  },
];

export const LandlordDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { landlordId } = useParams();

  const basePath = useMemo(
    () => (landlordId ? `/user/operator/${landlordId}/detail` : ''),
    [landlordId],
  );
  const { selectedIndex: selectedTab, goToTab } = useDetailRouteTabs({
    basePath,
    tabSlugs: LANDLORD_TAB_SLUGS,
  });

  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
  const landlordDetail = useSelector((state) => state.getIn(['user', 'landlord_detail'])) || null;
  const landlordDetailJSON = useImmutableJs(landlordDetail);
  const landlordDetailLoading = useSelector((state) =>
    state.getIn(['user', 'landlord_detail_loading']),
  );
  const landlordDetailError = useSelector((state) =>
    state.getIn(['user', 'landlord_detail_error']),
  );
  const {
    data: building,
    isLoading,
    hasError,
  } = useEntityDetailPageState({
    entityId: landlordId,
    entityData: landlordDetailJSON,
    isLoading: landlordDetailLoading,
    hasError: landlordDetailError,
  });

  const onClickBack = useCallback(() => {
    navigate('/user/operator');
  }, [navigate]);

  const onRetry = useCallback(() => {
    if (landlordId) dispatch(getLandlordDetailAction(landlordId));
  }, [landlordId, dispatch]);

  const onNavigateToApplications = useCallback(
    (prefilter) => {
      const base = `/user/operator/${landlordId}/detail/applications`;
      const path =
        prefilter != null && String(prefilter).length > 0
          ? `${base}?prefilter=${encodeURIComponent(prefilter)}`
          : base;
      navigate(path);
    },
    [landlordId, navigate],
  );

  useEffect(() => {
    if (!landlordId) {
      dispatch(failedNotif('Operator ID is missing.'));
      onClickBack();
      return;
    }
    dispatch(getLandlordDetailAction(landlordId));
  }, [dispatch, landlordId, onClickBack]);

  useHydrateDetailsOnce({
    type: 'landlord',
    data: landlordDetailJSON,
    id:
      landlordDetailJSON?.sf_landlord_id ||
      landlordDetailJSON?.landlord_id ||
      landlordDetailJSON?.id,
    buildPayload: ({ type, data }) => ({
      type,
      name: data?.name || data?.company_name,
      data,
    }),
  });

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      <DetailPageShell
        onBack={onClickBack}
        title={building ? maskDemoField(isDemo, 'landlord-name', building.name) : null}
        isLoading={isLoading}
        hasError={hasError}
        onRetry={onRetry}
        loadingLabel="Loading operator details..."
        skeletonVariant="fields"
      >
        {building ? <DetailCard landlord={building} isDemo={isDemo} /> : null}
      </DetailPageShell>

      {isLoading ? (
        <Box
          padding="24px 12px"
          borderRadius="12px"
          border="1px solid #EAEAEA"
          mt={1}
          sx={{ background: 'white' }}
        >
          <TabBarSkeleton tabCount={TABS.length} />
          <SectionPanelSkeleton />
        </Box>
      ) : hasError || !building ? null : (
        <Box
          padding="24px 12px"
          borderRadius="12px"
          border="1px solid #EAEAEA"
          mt={1}
          sx={{ background: 'white' }}
        >
          <TabWrapper>
            {TABS.map((tabItem, index) => (
              <Tab
                key={LANDLORD_TAB_SLUGS[index]}
                title={tabItem.title}
                icon={tabItem.icon}
                isSelected={selectedTab === index}
                onClick={() => goToTab(index)}
              />
            ))}
          </TabWrapper>

          <Box marginTop="20px">
            {selectedTab === 0 && (
              <ReportTab building={building} onNavigateToApplications={onNavigateToApplications} />
            )}
            {selectedTab === 1 && <BuildingTab building={building} />}
            {selectedTab === 2 && <ApplicationTab building={building} />}
            {selectedTab === 3 && <PolicyTab building={building} />}
            {selectedTab === 4 && <ClaimTab building={building} />}
            {selectedTab === 5 && <InvoiceTab building={building} />}
          </Box>
        </Box>
      )}
    </DashboardLayoutContainer>
  );
};

const TabWrapper = styled(Grid)`
  gap: 10px;
  grid-template-columns: repeat(7, 1fr);

  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
`;
